import React, {useState} from 'react';

import find from 'lodash/find';

import {AttachOverlay} from 'app/general/overlays';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {SimpleItemPickerListPopoverRTK} from 'app/general/widgets/SimpleItemPickerList';
import {
  convertLocationParamsToFetchParams,
  useInviteUser,
  useUsers,
} from 'app/user/api';
import {CreateUserModal} from 'app/user/components/CreateUserForm/CreateUserModal';
import {gt} from 'app/utils/i18n';

import type {NewInvitationType} from 'app/user/types';
import type {UserSerializedType} from 'sweet-types';

interface Props extends Pick<React.ComponentProps<'button'>, 'className'> {
  team: {id: number};
  title?: string;
  onConfirm?: (
    users: Array<UserSerializedType>,
    isManager: boolean
  ) => Promise<unknown>;
  initialGiveManagerAccess?: boolean;
}

type modalDataType = {
  selectedItems: Array<UserSerializedType>;
  refetchData: () => void;
  onChange: (selected: Array<UserSerializedType>) => unknown;
};

export function AddNewTeamMemberButton({
  team,
  title,
  onConfirm,
  className = 'btn btn-light',
  initialGiveManagerAccess = false,
}: Props) {
  const [giveManagerAccess, setManagerAccess] = useState(
    initialGiveManagerAccess
  );
  const [inviteUser] = useInviteUser();

  const [show, setShow] = useState(false);

  const [modalData, setModalData] = useState<modalDataType | undefined>();

  const addUsersToTeam =
    onConfirm != null
      ? onConfirm
      : (users: Array<UserSerializedType>, isManager) => {
          const invitations = users.map<NewInvitationType>((user) => ({
            send_mail: false,
            content_type: 'team',
            permission: isManager ? 'edit' : 'view',
            object_id: team.id,
            to_user_id: user.url,
          }));
          return inviteUser({invitations});
        };

  const toggleManagerAccessCheckbox = (isChecked: boolean) => {
    setManagerAccess(isChecked);
  };

  const getUserItemProps = (user: UserSerializedType) => {
    return {
      disabledText:
        find(user.teams, {
          id: team.id,
        }) != null
          ? gt('Already team member')
          : undefined,
    };
  };

  return (
    <>
      <CreateUserModal
        show={show}
        setShow={setShow}
        onCreateUser={(user) => {
          modalData?.onChange([...modalData.selectedItems, user]);
          setShow(false);
          return Promise.resolve(true);
        }}
      />
      <AttachOverlay
        trigger="click"
        overlay={({overlayProps}) => (
          <SimpleItemPickerListPopoverRTK
            showDescription={false}
            overlayProps={{...overlayProps, placement: 'left'}}
            additionalParams={{
              exclude_team_id: team.id,
              exclude_guests: '1',
            }}
            useDataHook={useUsers}
            paramsConverter={convertLocationParamsToFetchParams}
            addItemProps={getUserItemProps}
            onConfirm={(users) => addUsersToTeam(users, giveManagerAccess)}
            searchPlaceholder={gt('Search all members')}
            isPopover={true}
            isSmall={true}
          >
            {(selectedItems, refetchData, onChange) => (
              <div className="actions-block">
                {selectedItems.length > 0 ? (
                  <CheckboxButton
                    className="btn btn-sm btn-link"
                    isChecked={giveManagerAccess}
                    onClick={toggleManagerAccessCheckbox}
                  >
                    {gt('Give manager access')}
                  </CheckboxButton>
                ) : null}
                <button
                  onClick={() => {
                    setModalData({
                      selectedItems,
                      refetchData,
                      onChange,
                    });
                    setShow(true);
                  }}
                  className="btn btn-sm btn-link"
                >
                  {gt('Invite new member to account')}
                </button>
              </div>
            )}
          </SimpleItemPickerListPopoverRTK>
        )}
      >
        {(triggers) => (
          <button className={className} {...triggers}>
            <i className="fas fa-plus-circle mr-1" />
            {gt(title ?? 'Member')}
          </button>
        )}
      </AttachOverlay>
    </>
  );
}
